'use client';

import { useFormSubmissions } from '@commenda/api/form-builder/form-submissions/useFormSubmissions';
import {
  getFormsTemplate,
  useFormsTemplate,
} from '@commenda/api/form-builder/forms-templates';
import { useToggle } from '@commenda/kakashi';
import type { FormDSL, FormStep, TemplateMetadata } from '@commenda/types';
import {
  FORM_BUILDER_DEV_STORAGE_KEY,
  FORM_BUILDER_EDITOR_PATH,
  FORM_BUILDER_PREVIEW_PATH,
  FORM_BUILDER_TEMPLATES_PATH,
} from '@commenda/utils/form-builder';
import { useRouter } from 'next/router';
import React, {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { useNotification } from '../../hooks/useNotification';
import { DEFAULT_TEMPLATE_METADATA } from '../constants';
import { SubmissionsWarningDialog } from '../editor/SubmissionsWarningDialog';
import { useFormBuilder } from './useFormBuilder';

type TemplateKey = {
  key: string;
  name: string;
};

type FormEditorContextValue = {
  formBuilder: ReturnType<typeof useFormBuilder>;
  templateId?: string;
  isMounted: boolean;
  editorPath: string;
  templatesPath: string;
  previewPath: string;
  readOnly: boolean;
  getVersionsHref: (templateName: string) => string;
  getPreviewHref: (templateId: string) => string;
  showKeyManagement: boolean;
  isLoadingTemplate: boolean;
  templateError: unknown;
  devMode: boolean;

  activeStepIndex: number;
  setActiveStepIndex: (index: number) => void;

  showPreview: boolean;
  setShowPreview: (open: boolean) => void;
  showResetConfirm: boolean;
  setShowResetConfirm: (open: boolean) => void;
  showSaveDialog: boolean;
  setShowSaveDialog: (open: boolean) => void;
  showAddStepDialog: boolean;
  setShowAddStepDialog: (open: boolean) => void;
  setDevMode: (open: boolean) => void;
  saveDialogData: Partial<TemplateMetadata>;
  setSaveDialogData: React.Dispatch<
    React.SetStateAction<Partial<TemplateMetadata>>
  >;

  fileInputRef: RefObject<HTMLInputElement>;
  handleImport: (
    event: React.ChangeEvent<HTMLInputElement>
  ) => Promise<void> | void;

  handleQuickSave: () => Promise<void> | void;
  handleSaveTemplate: () => Promise<void> | void;
  handleGoToTemplates: () => void;
  handleGoToKeys: () => void;
  handleAddStepClick: () => void;
  handleAddNewStep: () => void;
  handleAddSavedStep: (stepId: string) => Promise<void>;

  resetFormAndClearTemplateId: () => void;
};

const FormEditorContext = createContext<FormEditorContextValue | null>(null);

export const useFormEditor = () => {
  const ctx = useContext(FormEditorContext);
  if (!ctx) {
    throw new Error('useFormEditor must be used within FormEditorProvider');
  }
  return ctx;
};

function hasRequiredField(dsl: FormDSL): boolean {
  for (const step of dsl.steps) {
    for (const module of step.modules) {
      for (const component of module.components) {
        const required = component.validation?.rules?.required;
        if (
          required === true ||
          (typeof required === 'object' && required.enabled)
        ) {
          return true;
        }
      }
    }
  }
  return false;
}

type FormEditorProviderProps = {
  children: ReactNode;
  editorPath?: string;
  templatesPath?: string;
  previewPath?: string;
  readOnly?: boolean;
  storageKey?: string;
  defaultDevMode?: boolean;
  // A host without a versions screen sends the back button somewhere it owns.
  getVersionsHref?: (templateName: string) => string;
  // Admin's preview reads `?id=`; a host whose route already binds `id` supplies its own href.
  getPreviewHref?: (templateId: string) => string;
  // A host without a keys screen hides both key entry points.
  showKeyManagement?: boolean;
};

export const FormEditorProvider = ({
  children,
  editorPath = FORM_BUILDER_EDITOR_PATH,
  templatesPath = FORM_BUILDER_TEMPLATES_PATH,
  previewPath = FORM_BUILDER_PREVIEW_PATH,
  readOnly = false,
  storageKey,
  defaultDevMode = true,
  getVersionsHref,
  getPreviewHref,
  showKeyManagement = true,
}: FormEditorProviderProps) => {
  const router = useRouter();
  const { setNotification } = useNotification();
  const { templateId: rawTemplateId } = router.query;

  const templateId =
    typeof rawTemplateId === 'string' ? rawTemplateId : undefined;

  const [isMounted, setIsMounted] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [saveDialogData, setSaveDialogData] = useState<
    Partial<TemplateMetadata>
  >(DEFAULT_TEMPLATE_METADATA);

  const showPreview = useToggle(false);
  const showResetConfirm = useToggle(false);
  const showSaveDialog = useToggle(false);
  const showAddStepDialog = useToggle(false);
  const showSubmissionsWarning = useToggle(false);
  const lastLoadedUpdatedAt = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingSaveAction = useRef<(() => Promise<void>) | null>(null);
  const [devMode, setDevModeState] = useState<boolean>(defaultDevMode);
  const { getItem, setItem } = useLocalStorage();

  useEffect(() => {
    // A host that opts out of dev mode stays out, whatever is in storage.
    if (!defaultDevMode) return;
    const stored = getItem(FORM_BUILDER_DEV_STORAGE_KEY);
    setDevModeState(stored ? stored === 'true' : true);
  }, [defaultDevMode]);

  const setDevMode = (value: boolean) => {
    if (!defaultDevMode) return;
    setDevModeState(value);
    setItem(FORM_BUILDER_DEV_STORAGE_KEY, String(value));
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const {
    template,
    isLoading: isLoadingTemplate,
    error: templateError,
    mutate: mutateTemplate,
  } = useFormsTemplate(templateId);

  const formBuilder = useFormBuilder({
    skipLocalStorage: !!templateId,
    storageKey,
  });

  // Check for existing submissions when template has an ID
  const { count: submissionsCount } = useFormSubmissions(
    formBuilder.templateMetadata?.id
      ? { formTemplateId: [formBuilder.templateMetadata.id] }
      : null
  );

  useEffect(() => {
    // Load template when it arrives from API, allowing re-load when updatedAt changes
    // This handles SWR revalidation returning fresh data after stale cache
    if (template && template.updatedAt !== lastLoadedUpdatedAt.current) {
      formBuilder.loadFromTemplate(template);
      lastLoadedUpdatedAt.current = template.updatedAt;
    }
  }, [template, formBuilder]);

  // Initialize save dialog from templateMetadata only when save dialog opens
  // (avoids overwriting key/keyName after user creates a key in CreateKeyModal)
  const prevSaveDialogOpen = useRef(false);
  useEffect(() => {
    const justOpened = showSaveDialog.value && !prevSaveDialogOpen.current;
    prevSaveDialogOpen.current = showSaveDialog.value;
    if (justOpened && formBuilder.templateMetadata) {
      const meta = formBuilder.templateMetadata;
      setSaveDialogData({
        name: meta.name,
        description: meta.description || '',
        status: meta.status,
        version: meta.version,
        versionNote: meta.versionNote || '',
        key: meta.key || '',
        keyName: meta.keyName || '',
      });
    }
  }, [showSaveDialog.value, formBuilder.templateMetadata]);

  // -------- Import handler --------
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await formBuilder.importFromJSON(file);
      setActiveStepIndex(0);
    } catch (error) {
      console.error('Failed to import:', error);
      // TODO: replace with toast from your design system
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to import file',
        message: 'Please check the file format and try again.',
      });
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const setShowSaveDialog = (open: boolean) => {
    if (open) {
      showSaveDialog.on();
    } else {
      showSaveDialog.off();
    }
  };

  const setShowResetConfirm = (open: boolean) => {
    if (open) {
      showResetConfirm.on();
    } else {
      showResetConfirm.off();
    }
  };

  const setShowAddStepDialog = (open: boolean) => {
    if (open) {
      showAddStepDialog.on();
    } else {
      showAddStepDialog.off();
    }
  };

  const setShowPreview = (open: boolean) => {
    if (open) {
      showPreview.on();
    } else {
      showPreview.off();
    }
  };

  // -------- Save handlers --------
  const handleSaveTemplate = async () => {
    if (!saveDialogData.name) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to save template',
        message: 'Please enter a template name',
      });
      return;
    }

    if (!hasRequiredField(formBuilder.dsl)) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to save template',
        message: 'Form must have at least one required field.',
      });
      return;
    }

    const result = await formBuilder.saveTemplateToAPI(
      saveDialogData as TemplateMetadata
    );

    if (result.success) {
      showSaveDialog.off();
      // Invalidate SWR cache so fresh data is available on refresh
      await mutateTemplate();

      if (!formBuilder.templateMetadata?.id && result.templateId) {
        router.replace(
          `${editorPath}?templateId=${result.templateId}`,
          undefined,
          { shallow: true }
        );
      }
    } else {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to save template',
        message: 'Please try again.',
      });
    }
  };

  const handleQuickSave = async () => {
    if (!hasRequiredField(formBuilder.dsl)) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to save template',
        message: 'Form must have at least one required field.',
      });
      return;
    }

    if (formBuilder.templateMetadata?.id) {
      // Use formBuilder.templateMetadata for quick save to ensure header changes are persisted
      const metadataToSave = {
        status: formBuilder.templateMetadata.status,
        key: formBuilder.templateMetadata.key || undefined,
      };

      // Check if template has existing submissions
      if (submissionsCount > 0) {
        // Store the save action to execute after user confirms
        pendingSaveAction.current = async () => {
          const currentTemplateId = formBuilder.templateMetadata?.id;
          const result = await formBuilder.saveTemplateToAPI(metadataToSave);

          if (result.success) {
            setNotification({
              show: true,
              valid: true,
              title: 'New version saved',
              message: 'A new version of the template has been saved.',
            });

            // Check if the template ID changed (versioning created a new template)
            if (result.templateId && result.templateId !== currentTemplateId) {
              // Load the new template directly from the response
              if (result.template) {
                formBuilder.loadFromTemplate(result.template);
                lastLoadedUpdatedAt.current = result.template.updatedAt;
              }
              // Update the URL to the new template ID
              router.replace(
                `${editorPath}?templateId=${result.templateId}`,
                undefined,
                { shallow: true }
              );
            } else {
              lastLoadedUpdatedAt.current = null;
              await mutateTemplate();
            }
          } else {
            setNotification({
              show: true,
              valid: false,
              title: 'Failed to save template',
              message: 'Please try again.',
            });
          }
        };
        // Show warning dialog
        showSubmissionsWarning.on();
        return;
      }

      // No submissions, proceed with save
      const result = await formBuilder.saveTemplateToAPI(metadataToSave);

      if (result.success) {
        // Invalidate SWR cache so fresh data is available on refresh
        await mutateTemplate();
      } else {
        setNotification({
          show: true,
          valid: false,
          title: 'Failed to save template',
          message: 'Please try again.',
        });
      }
    } else {
      showSaveDialog.on();
    }
  };

  const handleConfirmSubmissionsWarning = async () => {
    if (pendingSaveAction.current) {
      await pendingSaveAction.current();
      pendingSaveAction.current = null;
    }
  };

  // -------- Steps / templates actions --------
  const handleAddStepClick = () => {
    showAddStepDialog.on();
  };

  const handleAddNewStep = () => {
    formBuilder.addStep();
  };

  const handleAddSavedStep = async (stepId: string) => {
    const result = await getFormsTemplate(stepId);

    if (!result.success || !result.data?.formsTemplate?.dsl) {
      setNotification({
        show: true,
        valid: false,
        title: 'Failed to load step template',
        message: 'Please try again.',
      });
      return;
    }

    const stepTemplate = result.data.formsTemplate;
    formBuilder.addStepFromTemplate(stepTemplate.dsl as unknown as FormStep);
  };

  const handleGoToTemplates = () => {
    router.push(`${templatesPath}?tab=templates&t_offset=0`);
  };

  const handleGoToKeys = () => {
    router.push(`${templatesPath}?tab=keys&k_offset=0`);
  };

  const resetFormAndClearTemplateId = () => {
    formBuilder.resetForm();
    setActiveStepIndex(0);
    showResetConfirm.off();
    router.replace(editorPath, undefined, {
      shallow: true,
    });
  };

  const resolveVersionsHref = (templateName: string) =>
    getVersionsHref
      ? getVersionsHref(templateName)
      : `${templatesPath}/versions?name=${templateName}`;

  const resolvePreviewHref = (id: string) =>
    getPreviewHref ? getPreviewHref(id) : `${previewPath}?id=${id}`;

  const value: FormEditorContextValue = useMemo(
    () => ({
      formBuilder,
      templateId,
      isMounted,
      editorPath,
      templatesPath,
      previewPath,
      readOnly,
      getVersionsHref: resolveVersionsHref,
      getPreviewHref: resolvePreviewHref,
      showKeyManagement,
      isLoadingTemplate,
      templateError,
      devMode,
      setDevMode,

      activeStepIndex,
      setActiveStepIndex,

      showPreview: showPreview.value,
      setShowPreview,
      showResetConfirm: showResetConfirm.value,
      setShowResetConfirm,
      showSaveDialog: showSaveDialog.value,
      setShowSaveDialog,
      showAddStepDialog: showAddStepDialog.value,
      setShowAddStepDialog,

      saveDialogData,
      setSaveDialogData,

      fileInputRef,
      handleImport,

      handleQuickSave,
      handleSaveTemplate,
      handleGoToTemplates,
      handleGoToKeys,
      handleAddStepClick,
      handleAddNewStep,
      handleAddSavedStep,

      resetFormAndClearTemplateId,
    }),
    [
      formBuilder,
      templateId,
      devMode,
      isMounted,
      editorPath,
      templatesPath,
      previewPath,
      readOnly,
      resolveVersionsHref,
      resolvePreviewHref,
      showKeyManagement,
      isLoadingTemplate,
      templateError,
      activeStepIndex,
      setActiveStepIndex,
      showPreview,
      setShowPreview,
      showResetConfirm,
      setShowResetConfirm,
      showSaveDialog,
      setShowSaveDialog,
      showAddStepDialog,
      setShowAddStepDialog,
      saveDialogData,
      setDevMode,
      setSaveDialogData,
      fileInputRef,
      handleImport,
      handleQuickSave,
      handleSaveTemplate,
      handleGoToTemplates,
      handleAddStepClick,
      handleAddNewStep,
      handleAddSavedStep,
      resetFormAndClearTemplateId,
    ]
  );

  return (
    <FormEditorContext.Provider value={value}>
      {children}
      <SubmissionsWarningDialog
        open={showSubmissionsWarning.value}
        onOpenChange={(open) => {
          if (open) {
            showSubmissionsWarning.on();
          } else {
            showSubmissionsWarning.off();
            pendingSaveAction.current = null;
          }
        }}
        onConfirm={handleConfirmSubmissionsWarning}
      />
    </FormEditorContext.Provider>
  );
};
